import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

/**
 * MealogicHandoff — the pre-prepared-meal handoff, with one dismissible banner.
 *
 * WHY A BANNER AND NOT A GATE
 * ===========================
 * Vincent, 2026-10-05: "I believe a single dismissable banner with all relevant caveats would look
 * better then hard gates." He is right, and the reason matters. A hard gate on "Mealogic cannot
 * filter vitamin K" blocks a warfarin patient from a service that is still useful to them — we would
 * be making their dinner decision for them. A banner tells them the one thing they could not have
 * known and lets them decide. Informed, not blocked.
 *
 * WHAT MAKES IT SAFE RATHER THAN DECORATIVE
 * -----------------------------------------
 * The caveats are NOT written here. They come from `GET /api/mealogic/handoff`, which derives them
 * from the user's actual UHC — so this component cannot drift out of sync with what was really
 * filtered, and cannot show a reassuring message the backend did not authorise. Every caveat the
 * backend returns is rendered; none is summarised away.
 *
 * The high-severity ones (an allergen the vendor's closed list cannot express; vitamin K for someone
 * on warfarin) are shown EXPANDED. "Dismissible" means the banner can be closed, not that the
 * warning starts hidden behind a chevron.
 *
 * THE LINK IS A DISCLOSURE
 * ------------------------
 * Mealogic has no API — the workbook records API Access = 0 — so the filters travel in the query
 * string of the link. The backend returns a "What Mealogic will see" caveat on every filtered
 * handoff, and the user reads it before the link opens, not after. That is why this is two clicks
 * (open → review → go) rather than one.
 */
function MealogicHandoff({ fetchWithAuth, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/api/mealogic/handoff`);
        if (!res.ok) {
          // The server is the authority on whether this is safe to show. A 503 means the UHC could
          // not be loaded, and the correct response is to say so — never to fall back to an
          // unfiltered menu, which would look personalised and not be.
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setState({ loading: false, data: null,
                       error: body.detail || 'Pre-prepared meals are unavailable right now.' });
          }
          return;
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        if (!cancelled) {
          setState({ loading: false, data: null,
                     error: 'Pre-prepared meals are unavailable right now.' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [fetchWithAuth]);

  const { loading, error, data } = state;
  const caveats = data?.caveats || [];
  const applied = data?.applied || [];
  const highCaveats = caveats.filter((c) => c.severity === 'high');
  const infoCaveats = caveats.filter((c) => c.severity !== 'high');

  const openMenu = () => {
    if (!data?.url) return;
    // noopener/noreferrer: without it the new tab can reach back through window.opener, and the
    // referrer would hand Mealogic the Healist URL the user came from.
    window.open(data.url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div className="atc-overlay" role="dialog" aria-modal="true" aria-labelledby="mlg-title">
      <div className="atc-modal mlg-modal">
        <div className="atc-header">
          <h3 id="mlg-title">Order Pre-Prepared Meals</h3>
          <button onClick={onClose} className="rv-closeBtn" aria-label="Close">×</button>
        </div>

        <div className="atc-body mlg-body">
          {loading && <p className="text-helper">Matching the menu to your health profile…</p>}

          {error && <div className="mlg-caveat mlg-caveat--stop"><p>{error}</p></div>}

          {data && (
            <>
              {/* What was applied — shown first, because it is the answer to "is this for me?" */}
              {applied.length > 0 ? (
                <div className="mlg-applied">
                  <span className="mlg-sectionLabel">Filtered for you</span>
                  <ul>
                    {applied.map((a) => (
                      <li key={`${a.template}-${a.tag_id}`}>
                        <strong>{a.template.replace(/_/g, ' ')}</strong>
                        <span className="mlg-because">
                          {a.source === 'your stated preference'
                            ? ' — your stated preference'
                            : ` — because of ${a.because}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-helper">
                  Nothing in your health profile maps to a Mealogic filter, so this is their full menu.
                </p>
              )}

              {data.avoid?.length > 0 && (
                <div className="mlg-applied">
                  <span className="mlg-sectionLabel">Excluded from the menu</span>
                  <p className="mlg-avoidList">
                    {data.avoid.map((a) => a.replace(/_/g, ' ')).join(' · ')}
                  </p>
                </div>
              )}

              {/* The caveats. High severity is never collapsed. */}
              {!dismissed && caveats.length > 0 && (
                <div className="mlg-banner">
                  <div className="mlg-bannerHead">
                    <span className="mlg-sectionLabel">Before you order</span>
                    <button
                      className="mlg-dismiss"
                      onClick={() => setDismissed(true)}
                      aria-label="Dismiss these notes"
                    >
                      Dismiss
                    </button>
                  </div>

                  {highCaveats.map((c, i) => (
                    <div key={`h${i}`} className="mlg-caveat mlg-caveat--stop">
                      <span className="mlg-caveatLabel">{c.label}</span>
                      <p>{c.text}</p>
                    </div>
                  ))}

                  {infoCaveats.map((c, i) => (
                    <div key={`i${i}`} className="mlg-caveat mlg-caveat--note">
                      <span className="mlg-caveatLabel">{c.label}</span>
                      <p>{c.text}</p>
                    </div>
                  ))}
                </div>
              )}

              {dismissed && highCaveats.length > 0 && (
                // A dismissed SAFETY caveat still leaves a trace. The user chose to close the
                // banner, not to be told nothing.
                <p className="mlg-dismissedNote">
                  {highCaveats.length === 1 ? '1 note' : `${highCaveats.length} notes`} about what
                  this menu cannot filter were dismissed.{' '}
                  <button className="mlg-linkBtn" onClick={() => setDismissed(false)}>Show again</button>
                </p>
              )}
            </>
          )}
        </div>

        <div className="atc-footer">
          <button onClick={onClose} className="rv-closeFooterBtn">Not now</button>
          <button
            onClick={openMenu}
            className="atc-addBtn"
            disabled={loading || !!error || !data?.url}
          >
            Open Mealogic menu
          </button>
        </div>
      </div>
    </div>
  );
}

export default MealogicHandoff;
